/**
 * Integration Capabilities Model
 *
 * Defines what features/operations each integration platform supports.
 * This allows the UI to show/hide functionality based on what each platform can actually do.
 */

export enum IntegrationCapability {
    // Sequence/Campaign Management
    SEQUENCE_MANAGEMENT = 'sequence_management',          // Can create/edit/delete sequences
    SEQUENCE_READ_ONLY = 'sequence_read_only',           // Can only view sequences
    SEQUENCE_IMPORT = 'sequence_import',                 // Can import existing sequences

    // Contact/Lead Management
    CONTACT_MANAGEMENT = 'contact_management',           // Can add/edit/remove contacts
    CONTACT_IMPORT = 'contact_import',                   // Can import contacts
    CONTACT_EXPORT = 'contact_export',                   // Can export contacts

    // Analytics & Reporting
    ANALYTICS_BASIC = 'analytics_basic',                 // Basic metrics (opens, clicks, replies)
    ANALYTICS_ADVANCED = 'analytics_advanced',           // Advanced analytics (attribution, pipeline)
    REAL_TIME_SYNC = 'real_time_sync',                   // Real-time data synchronization

    // Messaging
    MESSAGE_TEMPLATES = 'message_templates',             // Supports message templates
    AI_PERSONALIZATION = 'ai_personalization',           // AI-powered personalization

    // Automation
    WORKFLOW_AUTOMATION = 'workflow_automation',         // Workflow/automation support
    TRIGGERED_ACTIONS = 'triggered_actions',             // Event-based triggers

    // OAuth & Authentication
    OAUTH_SUPPORT = 'oauth_support',                     // Uses OAuth (vs API key)
    API_KEY_AUTH = 'api_key_auth',                       // Uses API key authentication

    // Webhooks
    WEBHOOK_SUPPORT = 'webhook_support',                 // Supports webhooks
    WEBHOOK_REAL_TIME = 'webhook_real_time',            // Real-time webhook events
}

export interface IntegrationCapabilities {
    platformId: string;
    platformName: string;
    capabilities: IntegrationCapability[];
    limitations?: string[];                               // Known limitations
    documentationUrl?: string;
}

/**
 * Integration Capability Configuration
 * Central source of truth for what each integration platform supports
 */
export const INTEGRATION_CAPABILITIES: Record<string, IntegrationCapabilities> = {
    instantly: {
        platformId: 'instantly',
        platformName: 'Instantly.ai',
        capabilities: [
            IntegrationCapability.SEQUENCE_MANAGEMENT,
            IntegrationCapability.SEQUENCE_IMPORT,
            IntegrationCapability.CONTACT_MANAGEMENT,
            IntegrationCapability.CONTACT_IMPORT,
            IntegrationCapability.ANALYTICS_BASIC,
            IntegrationCapability.ANALYTICS_ADVANCED,
            IntegrationCapability.REAL_TIME_SYNC,
            IntegrationCapability.MESSAGE_TEMPLATES,
            IntegrationCapability.API_KEY_AUTH,
            IntegrationCapability.WEBHOOK_SUPPORT,
        ],
        documentationUrl: 'https://docs.instantly.ai'
    },

    smartlead: {
        platformId: 'smartlead',
        platformName: 'SmartLead',
        capabilities: [
            IntegrationCapability.SEQUENCE_MANAGEMENT,
            IntegrationCapability.SEQUENCE_IMPORT,
            IntegrationCapability.CONTACT_MANAGEMENT,
            IntegrationCapability.CONTACT_IMPORT,
            IntegrationCapability.ANALYTICS_BASIC,
            IntegrationCapability.MESSAGE_TEMPLATES,
            IntegrationCapability.API_KEY_AUTH,
        ],
        documentationUrl: 'https://docs.smartlead.ai'
    },

    apollo: {
        platformId: 'apollo',
        platformName: 'Apollo',
        capabilities: [
            IntegrationCapability.SEQUENCE_READ_ONLY,      // Apollo doesn't allow external sequence management
            IntegrationCapability.SEQUENCE_IMPORT,
            IntegrationCapability.CONTACT_IMPORT,
            IntegrationCapability.CONTACT_EXPORT,
            IntegrationCapability.ANALYTICS_BASIC,
            IntegrationCapability.API_KEY_AUTH,
        ],
        limitations: [
            'Cannot create or edit sequences via API',
            'Read-only access to sequence data'
        ],
        documentationUrl: 'https://apolloio.github.io/apollo-api-docs'
    },

    heyreach: {
        platformId: 'heyreach',
        platformName: 'HeyReach',
        capabilities: [
            IntegrationCapability.SEQUENCE_READ_ONLY,      // HeyReach doesn't allow external sequence management
            IntegrationCapability.SEQUENCE_IMPORT,
            IntegrationCapability.CONTACT_IMPORT,
            IntegrationCapability.ANALYTICS_BASIC,
            IntegrationCapability.API_KEY_AUTH,
        ],
        limitations: [
            'Cannot create or edit sequences via API',
            'Limited to importing existing campaigns'
        ],
        documentationUrl: 'https://docs.heyreach.io'
    },

    hubspot: {
        platformId: 'hubspot',
        platformName: 'HubSpot',
        capabilities: [
            IntegrationCapability.SEQUENCE_MANAGEMENT,
            IntegrationCapability.SEQUENCE_IMPORT,
            IntegrationCapability.CONTACT_MANAGEMENT,
            IntegrationCapability.CONTACT_IMPORT,
            IntegrationCapability.CONTACT_EXPORT,
            IntegrationCapability.ANALYTICS_BASIC,
            IntegrationCapability.ANALYTICS_ADVANCED,
            IntegrationCapability.REAL_TIME_SYNC,
            IntegrationCapability.MESSAGE_TEMPLATES,
            IntegrationCapability.WORKFLOW_AUTOMATION,
            IntegrationCapability.TRIGGERED_ACTIONS,
            IntegrationCapability.OAUTH_SUPPORT,
            IntegrationCapability.WEBHOOK_SUPPORT,
            IntegrationCapability.WEBHOOK_REAL_TIME,
        ],
        documentationUrl: 'https://developers.hubspot.com'
    },

    activecampaign: {
        platformId: 'activecampaign',
        platformName: 'Active Campaign',
        capabilities: [
            IntegrationCapability.SEQUENCE_MANAGEMENT,
            IntegrationCapability.SEQUENCE_IMPORT,
            IntegrationCapability.CONTACT_MANAGEMENT,
            IntegrationCapability.CONTACT_IMPORT,
            IntegrationCapability.ANALYTICS_BASIC,
            IntegrationCapability.MESSAGE_TEMPLATES,
            IntegrationCapability.WORKFLOW_AUTOMATION,
            IntegrationCapability.API_KEY_AUTH,
            IntegrationCapability.WEBHOOK_SUPPORT,
        ],
        documentationUrl: 'https://developers.activecampaign.com'
    },

    factor15: {
        platformId: 'factor15',
        platformName: 'Factor 15',
        capabilities: [
            IntegrationCapability.SEQUENCE_MANAGEMENT,
            IntegrationCapability.SEQUENCE_IMPORT,
            IntegrationCapability.CONTACT_MANAGEMENT,
            IntegrationCapability.ANALYTICS_BASIC,
            IntegrationCapability.API_KEY_AUTH,
        ],
        documentationUrl: 'https://factor15consulting.com/docs'
    },

    expandi: {
        platformId: 'expandi',
        platformName: 'Expandi',
        capabilities: [
            IntegrationCapability.SEQUENCE_READ_ONLY,
            IntegrationCapability.SEQUENCE_IMPORT,
            IntegrationCapability.ANALYTICS_BASIC,
            IntegrationCapability.API_KEY_AUTH,
        ],
        limitations: [
            'Limited API access for sequence management'
        ]
    },

    salesloft: {
        platformId: 'salesloft',
        platformName: 'SalesLoft',
        capabilities: [
            IntegrationCapability.SEQUENCE_MANAGEMENT,
            IntegrationCapability.SEQUENCE_IMPORT,
            IntegrationCapability.CONTACT_MANAGEMENT,
            IntegrationCapability.ANALYTICS_ADVANCED,
            IntegrationCapability.OAUTH_SUPPORT,
            IntegrationCapability.WEBHOOK_SUPPORT,
        ],
        documentationUrl: 'https://developers.salesloft.com'
    },

    constantcontact: {
        platformId: 'constantcontact',
        platformName: 'Constant Contact',
        capabilities: [
            IntegrationCapability.SEQUENCE_READ_ONLY,
            IntegrationCapability.SEQUENCE_IMPORT,
            IntegrationCapability.ANALYTICS_BASIC,
            IntegrationCapability.ANALYTICS_ADVANCED,
            IntegrationCapability.OAUTH_SUPPORT,
        ],
        limitations: [
            'Cannot create or edit email campaigns via integration',
            'Campaign creation must be done in Constant Contact UI',
            'Rate limited to 4 requests/second, 10,000/day'
        ],
        documentationUrl: 'https://developer.constantcontact.com'
    }
};

/**
 * Helper function to check if a platform has a specific capability
 */
export function hasCapability(platformId: string, capability: IntegrationCapability): boolean {
    const platformCapabilities = INTEGRATION_CAPABILITIES[platformId];
    if (!platformCapabilities) {
        return false;
    }
    return platformCapabilities.capabilities.includes(capability);
}

/**
 * Helper function to check if a platform has any of the specified capabilities
 */
export function hasAnyCapability(platformId: string, capabilities: IntegrationCapability[]): boolean {
    return capabilities.some(capability => hasCapability(platformId, capability));
}

/**
 * Helper function to check if a platform has all of the specified capabilities
 */
export function hasAllCapabilities(platformId: string, capabilities: IntegrationCapability[]): boolean {
    return capabilities.every(capability => hasCapability(platformId, capability));
}

/**
 * Get all capabilities for a platform
 */
export function getCapabilities(platformId: string): IntegrationCapabilities | undefined {
    return INTEGRATION_CAPABILITIES[platformId];
}

/**
 * Check if a platform supports sequence management (create/edit/delete)
 */
export function canManageSequences(platformId: string): boolean {
    return hasCapability(platformId, IntegrationCapability.SEQUENCE_MANAGEMENT);
}

/**
 * Check if a platform is read-only for sequences
 */
export function isSequenceReadOnly(platformId: string): boolean {
    return hasCapability(platformId, IntegrationCapability.SEQUENCE_READ_ONLY) &&
           !hasCapability(platformId, IntegrationCapability.SEQUENCE_MANAGEMENT);
}
